import { Container, Graphics, Rectangle, Text } from "pixi.js"

import { TextRenderer } from "../../text/text_renderer"
import {
    ACCENT,
    CARD_FILL,
    CARD_HOVER_FILL,
    CARD_RIM,
    PANEL_ALPHA,
    PANEL_FILL,
    RIM,
    RIM_DIM,
    SELECTED_FILL,
    TEXT,
    TEXT_DIM,
} from "./theme"

// The small set of pieces the character-creation screen is built from. Everything is drawn by hand
// (no filters -- see the budget-hardware note): glows are a fatter low-alpha copy of the shape
// underneath, which costs one extra draw call instead of an offscreen render pass.

export const text = (
    value: string,
    {
        size = 11,
        color = TEXT,
        bold = false,
        wrap,
    }: { size?: number; color?: number; bold?: boolean; wrap?: number } = {},
): Text => {
    const renderer = new TextRenderer().color(color).fontSize(size)
    if (bold) {
        renderer.bold()
    }
    if (wrap !== undefined) {
        // Set at build time: poking style.wordWrap afterwards re-rasterizes the Text.
        renderer.wordWrap(wrap)
    }
    return renderer.render(value) as Text
}

// Shrink, then clip, a label to fit a box. Proportional text can't be sized from a character count,
// so measure and act on the real width.
export const fitText = (label: Text, maxWidth: number, minScale = 0.78) => {
    if (label.width <= maxWidth || label.width === 0) {
        return
    }
    const scale = Math.max(minScale, maxWidth / label.width)
    label.scale.set(scale)
    if (label.width <= maxWidth) {
        return
    }
    const source = label.text
    for (let cut = source.length - 1; cut > 1; cut--) {
        label.text = `${source.slice(0, cut).trimEnd()}…`
        if (label.width <= maxWidth) {
            return
        }
    }
}

export const centerText = (label: Text, boxX: number, boxWidth: number, y: number) => {
    label.x = Math.round(boxX + (boxWidth - label.width) / 2)
    label.y = Math.round(y)
}

// Dark glass with a warm rim and a faint halo -- the panel every column of the screen sits in.
export const drawGlassPanel = (
    g: Graphics,
    x: number,
    y: number,
    w: number,
    h: number,
    { radius = 10, rim = RIM_DIM, rimAlpha = 0.75, glow = true, fill = PANEL_FILL, fillAlpha = PANEL_ALPHA } = {},
) => {
    if (glow) {
        g.beginFill(RIM, 0.05)
        g.drawRoundedRect(x - 4, y - 4, w + 8, h + 8, radius + 4)
        g.endFill()
    }
    g.lineStyle(1.5, rim, rimAlpha)
    g.beginFill(fill, fillAlpha)
    g.drawRoundedRect(x, y, w, h, radius)
    g.endFill()
    g.lineStyle(0)
}

// A section heading inside a panel: small caps-ish label with a hairline rule running off to the
// right, which reads as structure without spending vertical space on a box.
export const sectionHeading = (title: string, width: number): Container => {
    const container = new Container()
    const label = text(title.toUpperCase(), { size: 9, color: ACCENT, bold: true })
    label.alpha = 0.9
    container.addChild(label)

    const rule = new Graphics()
    rule.beginFill(RIM_DIM, 0.5)
    rule.drawRect(label.width + 8, Math.round(label.height / 2), Math.max(0, width - label.width - 8), 1)
    rule.endFill()
    container.addChild(rule)

    return container
}

interface ChoiceProps {
    width: number
    height: number
    selected: boolean
    onClick: () => void
    // Hovering a choice previews nothing by itself; it just lifts the chip.
    tooltip?: string
}

// One labelled option in a palette (a hairstyle, a cut of shirt, male/female...).
export class LabelChip extends Container {
    private bg: Graphics
    private hovered = false
    private readonly props: ChoiceProps

    constructor(label: string, props: ChoiceProps) {
        super()
        this.props = props
        this.bg = new Graphics()
        this.addChild(this.bg)

        const caption = text(label, { size: 10, color: props.selected ? 0x2a1c0c : TEXT })
        fitText(caption, props.width - 12)
        centerText(caption, 0, props.width, (props.height - caption.height) / 2)
        this.addChild(caption)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, props.width, props.height)
        this.on("pointerover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.draw()
        })
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw()
    }

    private draw() {
        const { width, height, selected } = this.props
        const g = this.bg
        g.clear()
        if (selected) {
            g.beginFill(ACCENT, 0.18)
            g.drawRoundedRect(-3, -3, width + 6, height + 6, 9)
            g.endFill()
            g.lineStyle(1.5, ACCENT, 1)
            g.beginFill(ACCENT, 0.92)
        } else {
            g.lineStyle(1, this.hovered ? RIM : CARD_RIM, this.hovered ? 0.9 : 0.6)
            g.beginFill(this.hovered ? CARD_HOVER_FILL : CARD_FILL, 0.95)
        }
        g.drawRoundedRect(0, 0, width, height, 6)
        g.endFill()
        g.lineStyle(0)
    }
}

// One colour in a palette. The swatch is the whole point, so the name only appears on hover.
export class ColorChip extends Container {
    private bg: Graphics
    private hovered = false
    private readonly props: ChoiceProps & { color: number }

    constructor(color: number, props: ChoiceProps) {
        super()
        this.props = { ...props, color }
        this.bg = new Graphics()
        this.addChild(this.bg)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, props.width, props.height)
        this.on("pointerover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.draw()
        })
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw()
    }

    private draw() {
        const { width, height, selected, color } = this.props
        const g = this.bg
        g.clear()
        if (selected || this.hovered) {
            g.beginFill(selected ? ACCENT : RIM, selected ? 0.3 : 0.16)
            g.drawRoundedRect(-4, -4, width + 8, height + 8, 9)
            g.endFill()
        }
        g.lineStyle(selected ? 2 : 1, selected ? ACCENT : this.hovered ? RIM : 0x000000, selected ? 1 : 0.55)
        g.beginFill(color, 1)
        g.drawRoundedRect(0, 0, width, height, 5)
        g.endFill()
        // Inner highlight: a hairline of light along the top edge so flat colours read as objects.
        g.lineStyle(1, 0xffffff, 0.18)
        g.moveTo(3, 2)
        g.lineTo(width - 3, 2)
        g.lineStyle(0)
    }
}

// A trait in the left-hand list: icon, name, and the choice currently made.
export class TraitRow extends Container {
    private bg: Graphics
    private hovered = false

    constructor(props: {
        icon: string
        label: string
        summary: string
        swatch?: number
        width: number
        height: number
        selected: boolean
        onClick: () => void
    }) {
        super()
        const { width, height, selected } = props
        this.bg = new Graphics()
        this.addChild(this.bg)

        const icon = text(props.icon, { size: 13 })
        icon.x = 9
        icon.y = Math.round((height - icon.height) / 2)
        this.addChild(icon)

        const name = text(props.label, { size: 11, color: selected ? 0xfff6e5 : TEXT, bold: selected })
        name.x = 32
        name.y = Math.round((height - name.height) / 2) - 5
        this.addChild(name)

        const summary = text(props.summary, { size: 9, color: selected ? ACCENT : TEXT_DIM })
        const summaryRight = props.swatch !== undefined ? width - 24 : width - 10
        fitText(summary, summaryRight - 32)
        summary.x = 32
        summary.y = name.y + 13
        this.addChild(summary)

        if (props.swatch !== undefined) {
            const dot = new Graphics()
            dot.lineStyle(1, 0x000000, 0.4)
            dot.beginFill(props.swatch, 1)
            dot.drawCircle(width - 15, height / 2, 6)
            dot.endFill()
            this.addChild(dot)
        }

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, width, height)
        this.on("pointerover", () => {
            this.hovered = true
            this.draw(props)
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.draw(props)
        })
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw(props)
    }

    private draw(props: { width: number; height: number; selected: boolean }) {
        const { width, height, selected } = props
        const g = this.bg
        g.clear()
        if (selected) {
            g.beginFill(SELECTED_FILL, 0.95)
            g.drawRoundedRect(0, 0, width, height, 7)
            g.endFill()
            // Amber spine on the selected row, pointing at the palette it opened.
            g.beginFill(RIM, 1)
            g.drawRoundedRect(0, 4, 3, height - 8, 1.5)
            g.endFill()
        } else if (this.hovered) {
            g.beginFill(CARD_HOVER_FILL, 0.8)
            g.drawRoundedRect(0, 0, width, height, 7)
            g.endFill()
        }
    }
}

// Footer action (Randomize / Next / ...). Amber-filled for the primary one, outlined otherwise.
export class PillButton extends Container {
    private bg: Graphics
    private hovered = false
    readonly boxWidth: number

    constructor(props: { label: string; primary?: boolean; onClick: () => void; height?: number }) {
        super()
        const height = props.height ?? 26
        const caption = text(props.label, { size: 11, color: props.primary ? 0x2a1c0c : TEXT, bold: !!props.primary })
        const width = Math.round(caption.width + 26)
        this.boxWidth = width

        this.bg = new Graphics()
        this.addChild(this.bg)
        centerText(caption, 0, width, (height - caption.height) / 2)
        this.addChild(caption)

        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, width, height)
        this.on("pointerover", () => {
            this.hovered = true
            this.draw(width, height, !!props.primary)
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.draw(width, height, !!props.primary)
        })
        this.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            props.onClick()
        })

        this.draw(width, height, !!props.primary)
    }

    private draw(width: number, height: number, primary: boolean) {
        const g = this.bg
        g.clear()
        if (primary) {
            g.beginFill(RIM, this.hovered ? 0.3 : 0.18)
            g.drawRoundedRect(-4, -4, width + 8, height + 8, height / 2 + 4)
            g.endFill()
            g.lineStyle(1.5, ACCENT, 1)
            g.beginFill(this.hovered ? 0xffdf9b : ACCENT, 1)
        } else {
            g.lineStyle(1.5, this.hovered ? RIM : CARD_RIM, this.hovered ? 1 : 0.8)
            g.beginFill(this.hovered ? CARD_HOVER_FILL : CARD_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, width, height, height / 2)
        g.endFill()
        g.lineStyle(0)
    }
}

// "‹ 2 / 4 ›" for a palette too big for one page.
export class Pager extends Container {
    readonly boxWidth: number

    constructor(props: { page: number; pages: number; onPage: (page: number) => void }) {
        super()
        const { page, pages, onPage } = props

        const arrow = (glyph: string, enabled: boolean, target: number) => {
            const button = new Container()
            const g = new Graphics()
            g.lineStyle(1, enabled ? CARD_RIM : 0x2a2018, 0.8)
            g.beginFill(CARD_FILL, enabled ? 0.9 : 0.4)
            g.drawRoundedRect(0, 0, 18, 18, 5)
            g.endFill()
            button.addChild(g)
            const caption = text(glyph, { size: 11, color: enabled ? TEXT : 0x584a3c })
            centerText(caption, 0, 18, (18 - caption.height) / 2)
            button.addChild(caption)
            if (enabled) {
                button.interactive = true
                button.cursor = "pointer"
                button.hitArea = new Rectangle(0, 0, 18, 18)
                button.on("pointerdown", (event: any) => {
                    event?.stopPropagation?.()
                    onPage(target)
                })
            }
            return button
        }

        const left = arrow("‹", page > 0, page - 1)
        this.addChild(left)

        const counter = text(`${page + 1} / ${pages}`, { size: 9, color: TEXT_DIM })
        counter.x = 24
        counter.y = Math.round((18 - counter.height) / 2)
        this.addChild(counter)

        const right = arrow("›", page < pages - 1, page + 1)
        right.x = 28 + counter.width
        this.addChild(right)

        this.boxWidth = right.x + 18
    }
}
